import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Minus, Plus, ShoppingBag } from "lucide-react";
import { fetchProduct } from "../lib/api";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const Bronze = () => {
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [error, setError] = useState(null);
  const { addItem, openCart, MAX_PER_PURCHASE } = useCart();

  useEffect(() => {
    fetchProduct("bronze")
      .then(setProduct)
      .catch((e) => setError(e.message));
  }, []);

  const handleAdd = () => {
    if (!product) return;
    addItem(product.id, qty);
    toast.success(`Added ${qty} × No. 2 — Bronze (${product.size}) to your cart`);
    openCart();
  };

  if (error) {
    return (
      <div className="pt-32 px-6 text-center text-red-400">
        Could not load product: {error}
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pt-32 px-6 text-center text-[var(--secondary-text)] uppercase tracking-label text-[11px]">
        Loading…
      </div>
    );
  }

  return (
    <div data-testid="bronze-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
        {/* Sticky image */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2, ease: [0.25, 0.1, 0.25, 1] }}
          className="relative"
        >
          <div className="lg:sticky lg:top-28">
            <div className="relative bg-black aspect-square flex items-center justify-center border hairline grain-overlay">
              <img
                src={product.image}
                alt={product.name}
                className="max-h-[88%] w-auto object-contain"
                data-testid="bronze-hero-image"
              />
            </div>
            <div className="flex items-center gap-4 mt-6">
              <img src="/mountain-mark.png" alt="" aria-hidden className="h-4 w-auto select-none" draggable={false} />
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Hand-crafted and Hand-bottled for your enjoyment. Edition 2026
              </span>
            </div>
          </div>
        </motion.div>

        {/* Details */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, delay: 0.2 }}
          className="lg:pt-12"
        >
          <span className="text-[10px] uppercase tracking-label gold-text">
            Our Second Composition
          </span>
          <h1 className="mt-5 font-serif text-5xl md:text-6xl leading-[1.0] tracking-tight">
            No. 2 - Bronze
          </h1>
          <p className="mt-4 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
            {product.tagline} · {product.size}
          </p>

          <div className="divider-gold mt-8 mb-8" />

          <p className="text-base text-[var(--secondary-text)] leading-loose max-w-lg">
            {product.description}
          </p>

          {/* Price + qty + add */}
          <div className="mt-12">
            <div className="flex items-baseline justify-between mb-10">
              <span
                className="font-serif text-4xl gold-text"
                data-testid="bronze-price"
              >
                ${product.price?.toFixed(2)}
              </span>
              <span className="text-[10px] uppercase tracking-label text-[var(--secondary-text)]">
                Limit {MAX_PER_PURCHASE} per order
              </span>
            </div>

            <div className="flex flex-col sm:flex-row items-stretch gap-5">
              <div className="inline-flex items-center self-start sm:self-auto">
                <button
                  className="qty-btn"
                  data-testid="bronze-decrease-btn"
                  onClick={() => setQty((q) => Math.max(1, q - 1))}
                  disabled={qty <= 1}
                  aria-label="Decrease quantity"
                >
                  <Minus size={14} />
                </button>
                <span
                  className="w-14 text-center text-sm tracking-label"
                  data-testid="bronze-qty"
                >
                  {qty}
                </span>
                <button
                  className="qty-btn"
                  data-testid="bronze-increase-btn"
                  onClick={() => setQty((q) => Math.min(MAX_PER_PURCHASE, q + 1))}
                  disabled={qty >= MAX_PER_PURCHASE}
                  aria-label="Increase quantity"
                >
                  <Plus size={14} />
                </button>
              </div>

              <button
                onClick={handleAdd}
                data-testid="bronze-add-to-cart-btn"
                className="btn-secondary inline-flex items-center justify-center gap-3 flex-1"
              >
                <ShoppingBag size={14} />
                Add to Cart — ${(product.price * qty).toFixed(2)}
              </button>
            </div>

            <ul className="mt-10 space-y-3 text-[11px] uppercase tracking-label text-[var(--secondary-text)]">
              <li>· 8+ hour long concentration</li>
              <li>· Hand-bottled in limited edition</li>
              <li>· $6.99 flat shipping · ships within United States</li>
            </ul>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Bronze;
